import { useState } from 'react';
import { useAuditLog } from '../../api/hooks/admin.hook';

const PAGE_SIZE = 25;

export const AuditPage = () => {
  const [offset, setOffset] = useState(0);
  const { data, isLoading } = useAuditLog(PAGE_SIZE, offset);

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;

  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-neutral-900">Audit Log</h1>
        <span className="text-xs text-neutral-400">{total} entries</span>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">
            Loading audit log...
          </div>
        ) : entries.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">
            No audit entries found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 text-left text-xs text-neutral-500 uppercase tracking-wide">
                <th className="px-4 py-2.5">Time</th>
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Action</th>
                <th className="px-4 py-2.5">Artifact</th>
                <th className="px-4 py-2.5">Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-neutral-50 hover:bg-neutral-50/50"
                >
                  <td className="px-4 py-2 text-xs text-neutral-500 whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-neutral-700 font-mono text-xs truncate max-w-[120px]">
                    {entry.user_id.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-2">
                    <ActionBadge action={entry.action} />
                  </td>
                  <td className="px-4 py-2 text-neutral-500 font-mono text-xs truncate max-w-[120px]">
                    {entry.artifact_id.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-2 text-xs text-neutral-400 truncate max-w-[200px]">
                    {Object.keys(entry.details).length > 0
                      ? JSON.stringify(entry.details)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-neutral-100">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!hasPrev}
              className="text-xs text-blue-600 hover:text-blue-700 disabled:text-neutral-300 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-neutral-400">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!hasNext}
              className="text-xs text-blue-600 hover:text-blue-700 disabled:text-neutral-300 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const actionColors: Record<string, string> = {
  created: 'bg-green-50 text-green-700',
  updated: 'bg-blue-50 text-blue-700',
  deleted: 'bg-red-50 text-red-600',
  shared: 'bg-purple-50 text-purple-700',
  unshared: 'bg-orange-50 text-orange-700',
  viewed: 'bg-neutral-50 text-neutral-600',
  visibility_changed: 'bg-amber-50 text-amber-700',
};

const ActionBadge = ({ action }: { action: string }) => (
  <span
    className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${
      actionColors[action] ?? 'bg-neutral-100 text-neutral-600'
    }`}
  >
    {action.replace('_', ' ')}
  </span>
);
